import { Box, Button, Card, CardContent, CardHeader, FormControlLabel, FormHelperText, Grid, MenuItem, Radio, Typography } from "@mui/material";
import { Drawer, useMediaQuery } from "@mui/material";
import { useFormik } from "formik";
import { useRecordContext } from "ra-core";
import { FC, useCallback, useMemo, useRef } from "react";
import * as Yup from 'yup'
import SelectField from "@/components/formik/SelectField";
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { RecPatch } from "@/dataProvider/tools";
import { CreateObfuscationRuleInput, InferredInfo, LastActivityKind, ObfuscationDataType, ObfuscationRuleDto, ObfuscationRuleType, UpdateObfuscationRuleInput } from "@/gen/api/encrypt";
import TableColumnRuleInputs from "./TableColumnRuleInputs";
import RadioGroupField from "@/components/formik/RadioGroupFields";
import ValuesListRuleInputs from "./ValuesListRuleInputs";
import { Inferred } from "./obfsRuleCompatibleMapping";
import ValueRangeRuleInput from "./ValueRangeRuleInputs";
import { useSuppressAlertUponSave } from "@/pages/obfuscation/EncryptionComposite";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_OBFUSCATION_RULES } from "@/constants";
import { disabledColor } from "@/utils/a11y";


interface Values { 
  submit: string; 
  ruleName: string; 
  description: string; 
  dataType: ObfuscationDataType; 
  ruleType: ObfuscationRuleType; 
  databaseId: '' | number; 
  tableId: '' | number; 
  fieldId: '' | number; 
  valuesList: string[];
  rangeValues: string[];
  rangeBorders: boolean[];
  inferredType: Inferred | null;
}

const validationSchema =
  Yup.object().shape({
    ruleName: Yup.string().max(100).required('Name is required'),
    description: Yup.string().max(512),
    ruleType: Yup.number().oneOf([
      ObfuscationRuleType.TableColumn, 
      ObfuscationRuleType.ValueList,
      ObfuscationRuleType.ValueRange,
      ObfuscationRuleType.ClearData
    ]).required('Rule Type is required'),
    dataType: Yup.number().oneOf([
      ObfuscationDataType.String, 
      ObfuscationDataType.DateTime,
      ObfuscationDataType.Integer,
      ObfuscationDataType.Numeric,
    ]).required('Data Type is required'),
  })

const validate = (values: Values) => {
  const errors = {} as any
  if (+values.ruleType === ObfuscationRuleType.TableColumn) {
    ['databaseId', 'tableId', 'fieldId'].forEach((id) => {
      if (!values[id as keyof Values]) {
        errors[id] = `${id.replace('Id', '')} is required`
      }
    })
  } else if (+values.ruleType === ObfuscationRuleType.ValueList) {
    if (values.valuesList.length === 0) {
      errors.valuesList = 'Value List is required'
    }
  }
  if (Object.keys(errors).length > 0) {
    return errors
  }
}

const EditObfuscationRule: FC<{
  open: boolean;
  onClose: () => void;
  clearDataRuleExists: boolean;
}> = ({ open, onClose, clearDataRuleExists }) => {
  const hasWritePermit = useHasPermit([MANAGE_OBFUSCATION_RULES])
  const match = useMediaQuery('(max-width: 630px)')
  const width = match ? 'calc(100vw - 50px)' : 680

  const record = useRecordContext<ObfuscationRuleDto&RecPatch>()
  const save = useSimpleSaveAction()
  const mode = record !== null && record !== undefined ? 'edit' : 'create'
  const newSave = useSuppressAlertUponSave(save, { databaseId: record?.databaseId as any, kind: LastActivityKind.ObfsRuleObsolete, actionArgs: [`${record?.ruleId}`] })
  
  const initialValues: Values = useMemo(() => ({
    submit: '',
    ruleName: record?.ruleName || '',
    description: record?.description || '',
    dataType: record?.dataType || ObfuscationDataType.String,
    ruleType: record?.ruleType || ObfuscationRuleType.TableColumn,
    databaseId: record?.databaseId || '',
    tableId: record?.tableId || '',
    fieldId: record?.fieldId || '',
    valuesList: record?.valuesList || [],
    rangeValues: record?.rangeValues || [],
    rangeBorders: record?.rangeBorders || [false, false],
    inferredType: null,
  }), [record])

  const recordRef = useRef<boolean | null>(!!record)

  const onSubmit: OnSubmit<Values> = (values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    if (!hasWritePermit) { return }
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }
    const part = {
      databaseId: null,
      tableId: null,
      fieldId: null,
      valuesList: null,
      rangeValues: null,
      rangeBorders: null,
      inferredType: values.inferredType as InferredInfo,
      ...(+values.ruleType === ObfuscationRuleType.TableColumn && {
        databaseId: values.databaseId || null,
        tableId: values.tableId || null,
        fieldId: values.fieldId || null,
      }),
      ...(+values.ruleType === ObfuscationRuleType.ValueList && {
        valuesList: values.valuesList,
      }),
      ...(+values.ruleType === ObfuscationRuleType.ValueRange && {
        rangeValues: values.rangeValues,
        rangeBorders: values.rangeBorders,
      }),
      ...(+values.ruleType === ObfuscationRuleType.ClearData && {
        inferredType: { type: 'string', length: 1 },
      }),
    }
    
    if (mode === 'create') {
      const payload: CreateObfuscationRuleInput = { 
        ruleName:    values.ruleName,
        description: values.description,
        ruleType: +values.ruleType,
        dataType: values.dataType,
        ...part,
      }
      save(payload, options)
    } else {
      const payload: UpdateObfuscationRuleInput = { 
        ruleId:      record.ruleId || 0,
        ruleName:    values.ruleName,
        description: values.description,
        ...part,
      }
      newSave(payload, options)
    }
  }

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema, validate })
  const { values, errors, isSubmitting, resetForm, setValues } = formikCtrl

  const handleClose = useCallback(() => { 
    onClose() 
    recordRef.current = null
    resetForm({
      values: initialValues,
    })
  }, [onClose, resetForm, initialValues])

  const handleRuleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues(prevValues => ({
      ...prevValues,
      ruleType: +e.target.value,
      databaseId: record?.databaseId || '',
      tableId: record?.tableId || '',
      fieldId: record?.fieldId || '',
      valuesList: record?.valuesList || [],
      rangeValues: record?.rangeValues || [],
      rangeBorders: record?.rangeBorders || [false, false],
      inferredType: null,
      ...(+e.target.value === ObfuscationRuleType.ValueRange && +prevValues.dataType === ObfuscationDataType.String && {
        dataType: ObfuscationDataType.DateTime,
      }),
      ...(+e.target.value === ObfuscationRuleType.ClearData && {
        dataType: ObfuscationDataType.String,
        inferredType: { type: 'string', length: 1 }
      })
    }))
  }
  return (
    
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >

      <OdForm formikCtrl={formikCtrl}>

        <Card>
          <CardHeader title={
            !hasWritePermit   ? "Obfuscation Rule Detail"
          : mode === 'create' ? "New Obfuscation Rule" 
          : "Edit Obfuscation Rule"} />
          <CardContent {...(!hasWritePermit && { sx: { 
            '& input.Mui-disabled, & div.Mui-disabled, & textarea.Mui-disabled': disabledColor
          } })} >
            <Grid
              container
              spacing={4}
            >
              <Grid item xs={12} sm={6} >
                <TextField label="Name*" name="ruleName" disabled={!hasWritePermit} />
              </Grid>
              <Grid item xs={12} >
                <TextField label="Description" name="description" multiline maxRows={3} disabled={!hasWritePermit} />
              </Grid>
              <Grid item xs={12} >
                <Box sx={{ ml: 2, mt: -1.5 }}>
                  <RadioGroupField label="Rule Type" name="ruleType" row onChange={handleRuleTypeChange} >
                    <FormControlLabel disabled={!hasWritePermit || mode === 'edit'} value={ObfuscationRuleType.TableColumn} control={<Radio />} label="Table Column" />
                    <FormControlLabel disabled={!hasWritePermit || mode === 'edit'} value={ObfuscationRuleType.ValueList} control={<Radio />} label="Value List" />
                    <FormControlLabel disabled={!hasWritePermit || mode === 'edit'} value={ObfuscationRuleType.ValueRange} control={<Radio />} label="Value Range" />
                    {(mode === 'edit' || !clearDataRuleExists) &&
                      <FormControlLabel disabled={!hasWritePermit || mode === 'edit'} value={ObfuscationRuleType.ClearData} control={<Radio />} label="Clear Data" />
                    }
                  </RadioGroupField>
                </Box>
              </Grid>
              {[ObfuscationRuleType.TableColumn, ObfuscationRuleType.ClearData].indexOf(+values.ruleType) < 0 &&
                <>
                  <Grid item xs={12} sm={6} >
                    <SelectField 
                      label="Data Type" 
                      name="dataType" 
                      disabled={!hasWritePermit || mode === 'edit'} 
                      onChange={e => { 
                        setValues(v => ({
                          ...v,
                          dataType: e.target.value as any,
                          fieldId: '',
                          inferredType: null,
                        }))
                      }}
                    >
                      {+values.ruleType !== ObfuscationRuleType.ValueRange &&
                        <MenuItem value={ObfuscationDataType.String}>String</MenuItem>}
                      <MenuItem value={ObfuscationDataType.DateTime}>DateTime</MenuItem>
                      <MenuItem value={ObfuscationDataType.Integer}>Integer</MenuItem>
                      <MenuItem value={ObfuscationDataType.Numeric}>Numeric</MenuItem>
                    </SelectField>
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    {+values.dataType === ObfuscationDataType.DateTime && 
                      <Typography variant="caption">Format Example: <b>2022-02-11</b></Typography>}
                  </Grid>
                </>
              }
              {+values.ruleType === ObfuscationRuleType.TableColumn &&
                <TableColumnRuleInputs />
              }
              {+values.ruleType === ObfuscationRuleType.ValueList && open &&
                <ValuesListRuleInputs />
              }
              {+values.ruleType === ObfuscationRuleType.ValueRange &&
                <ValueRangeRuleInput />
              }

            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
          </CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2
            }}
          >
            <Button
              color="primary"
              disabled={isSubmitting}
              onClick={handleClose}
              variant="outlined"
            >
              Cancel
            </Button>
            {hasWritePermit &&
              <Button
                color="primary"
                disabled={isSubmitting}
                type="submit"
                variant="contained"
              >
                Save
              </Button>}
          </Box>
        </Card>

      </OdForm>
    </Drawer>

  )
}

export default EditObfuscationRule;
